import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { comoErroDaApi } from '../api/erros';
import { Identificador } from './Identificador';

/**
 * A tela de erro com CAMINHO DE REPROCESSO.
 *
 * Três coisas aparecem sempre que existirem, porque são as três que a
 * operação usa para agir:
 *   - a mensagem em português (o que aconteceu);
 *   - o `codigo` em inglês e o `correlation_id` (como achar no log);
 *   - o botão de tentar de novo (o que fazer agora).
 *
 * O botão só aparece quando repetir pode ajudar. Oferecer "tentar de novo"
 * para um 422 é treinar a pessoa a clicar em botão que não faz nada.
 */
export function EstadoDeErro({
  erro,
  aoReprocessar,
  contexto,
}: {
  erro: unknown;
  aoReprocessar?: () => void;
  contexto?: string;
}): React.JSX.Element {
  const erroDaApi = comoErroDaApi(erro);
  const podeReprocessar = aoReprocessar !== undefined && erroDaApi.vaiAdiantarTentarDeNovo;

  return (
    <Alert severity="error" variant="outlined" role="alert" sx={{ m: 1 }}>
      <AlertTitle>{contexto ?? 'Não foi possível carregar'}</AlertTitle>

      <Typography variant="body2">{erroDaApi.message}</Typography>

      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {erroDaApi.codigo !== null ? (
          <Box>
            <Typography variant="caption" color="text.secondary" component="span">
              Código:{' '}
            </Typography>
            <Identificador valor={erroDaApi.codigo} />
          </Box>
        ) : null}

        {erroDaApi.correlationId !== null ? (
          <Box>
            <Typography variant="caption" color="text.secondary" component="span">
              correlation_id:{' '}
            </Typography>
            <Identificador
              valor={erroDaApi.correlationId}
              titulo="Este é o identificador que a operação usa para achar a execução no log. Ele é carregado, nunca regenerado."
            />
          </Box>
        ) : null}
      </Stack>

      {podeReprocessar ? (
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<RefreshOutlined />}
          onClick={aoReprocessar}
          sx={{ mt: 1.5 }}
        >
          Tentar de novo
        </Button>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Repetir não vai mudar o resultado. Leve o correlation_id para a operação.
        </Typography>
      )}
    </Alert>
  );
}
